import React, { useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/common/EmptyState";
import { RagBadge } from "@/components/common/RagBadge";
import { LeaveRequestDialog } from "@/components/people/LeaveRequestDialog";
import { LedgerDialog } from "@/components/people/LedgerDialog";
import { fmtDate } from "@/lib/format";
import { fmtDays, fmtH, LEAVE_STATUS, requestWhen, requestSize } from "@/lib/people";
import { toast } from "sonner";

function BalanceCard({ b, dayHours, onHistory }) {
  return (
    <div className="amden-card p-5" data-testid={`leave-balance-${b.leave_type_id}-${b.year_label}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#0B1729]"><span className="h-2.5 w-2.5 rounded-full" style={{ background: b.colour }} /> {b.type_name}</div>
        <span className="text-xs text-[#1E2D44]/45">{b.year_label}</span>
      </div>
      {b.has_entitlement ? (
        <>
          <div className="mt-3 font-display text-3xl font-bold text-[#0B1729]" data-testid="leave-available">{fmtDays(b.available, dayHours)}</div>
          <div className="text-xs text-[#1E2D44]/50">available · {fmtH(b.available)}</div>
          <div className="mt-4 grid grid-cols-4 gap-2 text-center text-[11px] text-[#1E2D44]/55">
            {[["Entitlement", b.entitlement], ["Adjusted", b.adjustments], ["Booked", b.booked], ["Pending", b.pending]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-[#F7F9FC] px-1 py-2"><div className="text-sm font-semibold text-[#0B1729]">{fmtDays(v, dayHours).replace(/ days?/, "d")}</div>{l}</div>
            ))}
          </div>
          <button onClick={onHistory} className="mt-3 text-xs font-medium text-[#4A78A8] hover:underline" data-testid={`leave-history-${b.leave_type_id}-${b.year_label}`}>See balance history</button>
        </>
      ) : <p className="mt-3 text-sm text-[#1E2D44]/50">Your entitlement hasn't been set yet. Ask your people administrator.</p>}
    </div>
  );
}

function RequestRow({ r, today, onChanged }) {
  const st = LEAVE_STATUS[r.status];
  const canWithdraw = r.status === "pending";
  const canCancel = r.status === "approved" && r.start_date >= today;
  const act = async () => {
    try {
      await api.post(`/workspace/leave/requests/${r.id}/cancel`, {});
      toast.success(canWithdraw ? "Request withdrawn" : "Leave cancelled — your balance has been restored");
      onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="amden-card flex flex-col gap-2 p-4 sm:flex-row sm:items-center" data-testid={`my-leave-${r.id}`}>
      <span className="hidden h-9 w-1.5 rounded-full sm:block" style={{ background: r.colour }} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[#0B1729]">{r.type_name} · {requestWhen(r)}</div>
        <div className="text-xs text-[#1E2D44]/55">
          {requestSize(r)}{r.approver_name ? ` · approver ${r.approver_name}` : ""}{r.decision_note ? ` · “${r.decision_note}”` : ""}
        </div>
      </div>
      <RagBadge rag={st.rag} testId={`my-leave-status-${r.id}`}>{st.label}</RagBadge>
      {(canWithdraw || canCancel) && (
        <Button size="sm" variant="outline" onClick={act} data-testid={`my-leave-cancel-${r.id}`}>{canWithdraw ? "Withdraw" : "Cancel"}</Button>
      )}
    </div>
  );
}

export function MyLeaveTab({ orgId, data: d, onChanged: changed }) {
  const { user } = useAuth();
  const [asking, setAsking] = useState(false);
  const [history, setHistory] = useState(null);
  if (!d.has_profile || !d.has_pattern) {
    return <EmptyState icon="CalendarOff" title="Leave isn't set up for you yet" description="Your people administrator needs to add your work pattern before you can book time off." testId="my-leave-not-ready" />;
  }
  const upcoming = d.requests.filter((r) => ["pending", "approved"].includes(r.status) && r.end_date >= d.today);
  const past = d.requests.filter((r) => !upcoming.includes(r));

  return (
    <div data-testid="my-leave-tab">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-[#1E2D44]/60">Balances are kept in hours and shown in days using your {d.day_hours}h working day.</p>
        <Button onClick={() => setAsking(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="request-leave-btn">
          <Icons.Plane size={16} className="mr-1.5" /> Request time off
        </Button>
      </div>
      <div className="mb-7 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {d.balances.map((b) => <BalanceCard key={`${b.leave_type_id}-${b.year_label}`} b={b} dayHours={d.day_hours} onHistory={() => setHistory(b)} />)}
        <div className="amden-card p-5" data-testid="upcoming-holidays">
          <div className="mb-2 text-sm font-semibold text-[#0B1729]">Next public holidays</div>
          {d.upcoming_holidays.length === 0 ? <p className="text-xs text-[#1E2D44]/45">None set.</p> : (
            <ul className="space-y-1.5 text-sm">{d.upcoming_holidays.map((h) => <li key={h.id} className="flex justify-between gap-2"><span className="text-[#1E2D44]/80">{h.name}</span><span className="text-xs text-[#1E2D44]/50">{fmtDate(h.date)}</span></li>)}</ul>
          )}
        </div>
      </div>
      <h3 className="mb-2.5 font-display text-sm font-semibold text-[#0B1729]">Booked and requested</h3>
      {upcoming.length === 0 ? <p className="mb-6 rounded-xl border border-dashed border-[#D2D9E3] px-4 py-5 text-center text-sm text-[#1E2D44]/45">Nothing coming up.</p> : (
        <div className="mb-6 space-y-2" data-testid="my-leave-upcoming">{upcoming.map((r) => <RequestRow key={r.id} r={r} today={d.today} onChanged={changed} />)}</div>
      )}
      {past.length > 0 && <>
        <h3 className="mb-2.5 font-display text-sm font-semibold text-[#0B1729]">Earlier</h3>
        <div className="space-y-2" data-testid="my-leave-past">{past.slice(0, 12).map((r) => <RequestRow key={r.id} r={r} today={d.today} onChanged={changed} />)}</div>
      </>}
      {asking && <LeaveRequestDialog orgId={orgId} types={d.types} onClose={() => setAsking(false)} onSaved={() => { setAsking(false); changed(); }} />}
      {history && <LedgerDialog orgId={orgId} userId={user.id} type={{ id: history.leave_type_id, name: history.type_name }} year={history.leave_year}
        yearLabel={history.year_label} dayHours={d.day_hours} onClose={() => setHistory(null)} />}
    </div>
  );
}
